import React, { useId } from 'react';

import { CardPlateProps, GLOW, GLOW_INNER, GLOW_OUTER, STROKE } from './cardPlateTokens';

/**
 * The category card's plate on web, drawn as one SVG: outer glow, fill, inner glow and the gold
 * stroke share a coordinate space, so they stay aligned wherever the card lands.
 *
 * The cards sit at fractional positions, and Chrome snaps CSS boxes to device pixels but SVG
 * content to whole CSS pixels. Mixing them (CSS glows and fill, SVG stroke) left the stroke up to
 * 1.5 device px off the fill, showing a dark sliver outside it; building the stroke from boxes
 * instead snapped it into a heavy whole-pixel band that stepped around the corners.
 *
 * Blur is a Gaussian with stdDeviation = CSS blur / 2, the same as box-shadow. Glow changes on
 * press are not eased (SVG filter values don't transition); the press scale still animates.
 */
const CardPlate = ({
	pressed,
	width: w,
	height: h,
	radius: r,
	strokeWidth: sw,
	glowUnit,
	outerGlowOpacity,
	fill,
}: CardPlateProps) => {
	const id = useId().replace(/[^a-zA-Z0-9]/g, '');
	const glow = pressed ? GLOW.pressed : GLOW.rest;
	const u = (value: number) => value * glowUnit;
	const outerSpread = u(glow.outerSpread);
	const innerSpread = u(glow.innerSpread);
	// Room for the outer glow: a Gaussian is spent by ~3 standard deviations.
	const pad = Math.ceil(u(GLOW.pressed.outerBlur) * 1.5 + u(GLOW.pressed.outerSpread));
	// The inner glow blurs a frame that reaches well past the card, so its edge is solid red.
	const frame = pad;
	const holeR = Math.max(r - innerSpread, 0);
	const hole = roundedRectPath(
		innerSpread,
		innerSpread,
		w - 2 * innerSpread,
		h - 2 * innerSpread,
		holeR,
	);

	return (
		<svg
			aria-hidden
			width={w + 2 * pad}
			height={h + 2 * pad}
			viewBox={`${-pad} ${-pad} ${w + 2 * pad} ${h + 2 * pad}`}
			style={{
				position: 'absolute',
				left: -pad,
				top: -pad,
				pointerEvents: 'none',
				overflow: 'visible',
			}}
		>
			<defs>
				<linearGradient id={`fill${id}`} x1='0' y1='0' x2='0' y2='1'>
					<stop offset='0' stopColor={fill[0]} />
					<stop offset='1' stopColor={fill[1]} />
				</linearGradient>
				<linearGradient id={`stroke${id}`} x1='0' y1='0' x2='0' y2='1'>
					<stop offset='0' stopColor={STROKE[0]} />
					<stop offset='1' stopColor={STROKE[1]} />
				</linearGradient>
				<filter id={`outer${id}`} x='-50%' y='-50%' width='200%' height='200%'>
					<feGaussianBlur stdDeviation={u(glow.outerBlur) / 2} />
				</filter>
				<filter id={`inner${id}`} x='-50%' y='-50%' width='200%' height='200%'>
					<feGaussianBlur stdDeviation={u(glow.innerBlur) / 2} />
				</filter>
				<clipPath id={`card${id}`}>
					<rect width={w} height={h} rx={r} ry={r} />
				</clipPath>
			</defs>

			{/* Outer glow: the card shape grown by the spread, blurred, behind the fill. */}
			<rect
				x={-outerSpread}
				y={-outerSpread}
				width={w + 2 * outerSpread}
				height={h + 2 * outerSpread}
				rx={r + outerSpread}
				ry={r + outerSpread}
				fill={`rgba(${GLOW_OUTER}, ${outerGlowOpacity})`}
				filter={`url(#outer${id})`}
			/>
			<rect width={w} height={h} rx={r} ry={r} fill={`url(#fill${id})`} />
			{/* Inner glow: red everywhere outside the card (shrunk by the spread), blurred, clipped
			    to the card and added with plus-lighter, like an inset box-shadow. */}
			<g clipPath={`url(#card${id})`} style={PLUS_LIGHTER}>
				<path
					d={`M${-frame} ${-frame}H${w + frame}V${h + frame}H${-frame}Z ${hole}`}
					fillRule='evenodd'
					fill={GLOW_INNER}
					filter={`url(#inner${id})`}
				/>
			</g>
			{/* Gold stroke inside the edge, over both glows. */}
			<rect
				x={sw / 2}
				y={sw / 2}
				width={w - sw}
				height={h - sw}
				rx={r - sw / 2}
				ry={r - sw / 2}
				fill='none'
				stroke={`url(#stroke${id})`}
				strokeWidth={sw}
			/>
		</svg>
	);
};

const PLUS_LIGHTER = { mixBlendMode: 'plus-lighter' } as const;

const roundedRectPath = (x: number, y: number, w: number, h: number, r: number) =>
	`M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + h - r}` +
	`A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}A${r} ${r} 0 0 1 ${x} ${y + h - r}` +
	`V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`;

export default CardPlate;
